(() => {
  'use strict';

  if (window.YagaImageZoom) return;

  const SELECTORS = {
    button: '[data-yaga-zoom-button]',
    frame: '[data-yaga-zoom-frame]',
    image: '.yaga-zoom-image',
  };
  const ACTIVE_CLASS = 'is-yaga-zoomed';
  const DRAGGING_CLASS = 'is-yaga-dragging';
  const HINT_CLASS = 'is-yaga-hint-visible';
  const preloadCache = new Map();
  const hintTimers = new WeakMap();

  function getFrame(button) {
    return button.closest(SELECTORS.frame) || button.parentElement?.querySelector(SELECTORS.frame);
  }

  function getButton(frame) {
    return (
      frame.querySelector(SELECTORS.button) ||
      Array.from(frame.parentElement?.children || []).find((element) => element.matches?.(SELECTORS.button))
    );
  }

  function getImage(frame) {
    return frame.querySelector(SELECTORS.image);
  }

  function setButtonState(button, active) {
    if (!button) return;
    button.setAttribute('aria-pressed', String(active));
    button.setAttribute(
      'aria-label',
      active ? button.dataset.yagaZoomResetLabel : button.dataset.yagaZoomOpenLabel,
    );
  }

  function setOrigin(frame, clientX, clientY) {
    const bounds = frame.getBoundingClientRect();
    if (!bounds.width || !bounds.height) return;

    const x = Math.min(100, Math.max(0, ((clientX - bounds.left) / bounds.width) * 100));
    const y = Math.min(100, Math.max(0, ((clientY - bounds.top) / bounds.height) * 100));
    frame.style.setProperty('--yaga-zoom-origin-x', `${x}%`);
    frame.style.setProperty('--yaga-zoom-origin-y', `${y}%`);
    frame.dataset.yagaZoomOriginX = String(x);
    frame.dataset.yagaZoomOriginY = String(y);
  }

  function showHint(frame, button) {
    const label = button.dataset.yagaZoomInlineHint;
    if (!label) return;

    const currentTimer = hintTimers.get(frame);
    if (currentTimer) window.clearTimeout(currentTimer);

    frame.dataset.yagaZoomHint = label;
    frame.classList.add(HINT_CLASS);
    const timer = window.setTimeout(() => {
      frame.classList.remove(HINT_CLASS);
      hintTimers.delete(frame);
    }, 1700);
    hintTimers.set(frame, timer);
  }

  function resetFrame(frame) {
    if (!frame) return;

    const timer = hintTimers.get(frame);
    if (timer) window.clearTimeout(timer);
    hintTimers.delete(frame);

    frame.classList.remove(ACTIVE_CLASS, DRAGGING_CLASS, HINT_CLASS);
    frame.style.removeProperty('--yaga-zoom-origin-x');
    frame.style.removeProperty('--yaga-zoom-origin-y');
    frame.style.removeProperty('--yaga-zoom-scale');
    delete frame.dataset.yagaZoomOriginX;
    delete frame.dataset.yagaZoomOriginY;
    delete frame.dataset.yagaDragging;
    delete frame.dataset.yagaZoomHint;
    setButtonState(getButton(frame), false);
  }

  function resetOtherFrames(currentFrame) {
    document.querySelectorAll(`${SELECTORS.frame}.${ACTIVE_CLASS}`).forEach((frame) => {
      if (frame !== currentFrame) resetFrame(frame);
    });
  }

  function preload(source) {
    if (!source) return Promise.resolve();
    if (preloadCache.has(source)) return preloadCache.get(source);

    const promise = new Promise((resolve, reject) => {
      const image = new Image();
      image.decoding = 'async';
      image.onload = () => resolve(image);
      image.onerror = reject;
      image.src = source;
    });

    preloadCache.set(source, promise);
    return promise;
  }

  async function upgradeImage(button, image) {
    const source = button.dataset.yagaZoomSrc;
    if (!source || image.dataset.yagaZoomSource === source) return;

    button.setAttribute('aria-busy', 'true');
    try {
      await preload(source);
      image.removeAttribute('srcset');
      image.removeAttribute('sizes');
      image.src = source;
      image.dataset.yagaZoomSource = source;
    } catch (error) {
      console.warn('YAGA image zoom could not load the larger image.', error);
    } finally {
      button.removeAttribute('aria-busy');
    }
  }

  async function activateFrame(button, frame, image) {
    resetOtherFrames(frame);
    await upgradeImage(button, image);

    frame.style.setProperty('--yaga-zoom-scale', button.dataset.yagaZoomScale || '2.4');
    frame.classList.add(ACTIVE_CLASS);
    setButtonState(button, true);
    image.setAttribute('draggable', 'false');

    const bounds = frame.getBoundingClientRect();
    setOrigin(frame, bounds.left + bounds.width / 2, bounds.top + bounds.height / 2);
    showHint(frame, button);
  }

  async function toggleFrame(button) {
    const frame = getFrame(button);
    const image = frame && getImage(frame);
    if (!frame || !image) return;

    if (frame.classList.contains(ACTIVE_CLASS)) {
      resetFrame(frame);
      return;
    }

    await activateFrame(button, frame, image);
  }

  document.addEventListener(
    'click',
    (event) => {
      const button = event.target.closest?.(SELECTORS.button);
      if (button) {
        event.preventDefault();
        event.stopPropagation();
        toggleFrame(button);
        return;
      }

      const frame = event.target.closest?.(SELECTORS.frame);
      if (!frame) return;

      if (frame.classList.contains(ACTIVE_CLASS)) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      if (!frame.closest('product-info')) return;
      const frameButton = getButton(frame);
      if (!frameButton) return;

      event.preventDefault();
      event.stopPropagation();
      toggleFrame(frameButton);
    },
    true,
  );

  document.addEventListener('pointerdown', (event) => {
    if (event.target.closest?.(SELECTORS.button)) return;

    const frame = event.target.closest?.(`${SELECTORS.frame}.${ACTIVE_CLASS}`);
    if (!frame) return;

    event.preventDefault();
    frame.dataset.yagaDragging = String(event.pointerId);
    frame.classList.add(DRAGGING_CLASS);
    frame.setPointerCapture?.(event.pointerId);
    setOrigin(frame, event.clientX, event.clientY);
  });

  document.addEventListener(
    'pointermove',
    (event) => {
      let frame;
      if (event.pointerType === 'mouse') {
        frame = event.target.closest?.(`${SELECTORS.frame}.${ACTIVE_CLASS}`);
      } else {
        frame = document.querySelector(
          `${SELECTORS.frame}.${ACTIVE_CLASS}[data-yaga-dragging="${event.pointerId}"]`,
        );
      }
      if (!frame) return;

      if (event.pointerType !== 'mouse') event.preventDefault();
      setOrigin(frame, event.clientX, event.clientY);
    },
    { passive: false },
  );

  function endDrag(event) {
    const frame = document.querySelector(
      `${SELECTORS.frame}.${DRAGGING_CLASS}[data-yaga-dragging="${event.pointerId}"]`,
    );
    if (!frame) return;

    frame.classList.remove(DRAGGING_CLASS);
    delete frame.dataset.yagaDragging;
    frame.releasePointerCapture?.(event.pointerId);
  }

  document.addEventListener('pointerup', endDrag);
  document.addEventListener('pointercancel', endDrag);

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      document.querySelectorAll(`${SELECTORS.frame}.${ACTIVE_CLASS}`).forEach((frame) => resetFrame(frame));
      return;
    }

    const button = document.activeElement?.closest?.(SELECTORS.button);
    const frame = button && getFrame(button);
    if (!frame?.classList.contains(ACTIVE_CLASS)) return;

    const changes = {
      ArrowLeft: [-8, 0],
      ArrowRight: [8, 0],
      ArrowUp: [0, -8],
      ArrowDown: [0, 8],
    };
    const change = changes[event.key];
    if (!change) return;

    event.preventDefault();
    const x = Math.min(100, Math.max(0, Number(frame.dataset.yagaZoomOriginX || 50) + change[0]));
    const y = Math.min(100, Math.max(0, Number(frame.dataset.yagaZoomOriginY || 50) + change[1]));
    frame.style.setProperty('--yaga-zoom-origin-x', `${x}%`);
    frame.style.setProperty('--yaga-zoom-origin-y', `${y}%`);
    frame.dataset.yagaZoomOriginX = String(x);
    frame.dataset.yagaZoomOriginY = String(y);
  });

  window.YagaImageZoom = {
    close() {
      document.querySelectorAll(`${SELECTORS.frame}.${ACTIVE_CLASS}`).forEach((frame) => resetFrame(frame));
    },
    reset() {
      document.querySelectorAll(`${SELECTORS.frame}.${ACTIVE_CLASS}`).forEach((frame) => resetFrame(frame));
    },
  };
})();
